import { useMemo } from 'react';
import { useRenderedCodeOptions } from 'ts/commons/code/RenderedCode.context';
import { isCodeLineSegment, type LineSegment } from 'ts/commons/code/RenderedCode.segments';
import { RegexUtils } from 'ts/commons/RegexUtils';
import { ELanguage } from 'typedefs/ELanguage';
import { createStore, useStore } from 'zustand';

/** A type for an identifier that can be focused in the rendered code */
type FocusableIdentifier = string;

/** Used to keep track of which braces belong together (for the brace highlighting feature) */
type FocusableBrace = {
	pairedBraceId: string;
	firstBraceId: string;
};

/**
 * Code entities that can be focused by the user (e.g., by clicking on an identifier or brace, which highlights all
 * other occurrences)
 */
type FocusableEntity = FocusableIdentifier | FocusableBrace;

/**
 * State to be kept in a zustand store describing a focused code token which can either be a pair of braces or an
 * identifier.
 */
export type FocusedCodeTokenState = {
	/** The currently selected identifier/brace or null if none is selected. */
	focusedEntity: FocusableEntity | null;
	/** Focuses the given segment or unfocuses it if it currently has the focus. */
	toggleEntityFocus: (segment: LineSegment) => void;
};

/** Stores which token currently is focused. */
export const createFocusedCodeTokenStore = () =>
	createStore<FocusedCodeTokenState>((set, get) => ({
		focusedEntity: null,
		toggleEntityFocus: (segment: LineSegment) => {
			const focusedEntity = get().focusedEntity;
			let newFocusedEntity: FocusableEntity | null;
			if (segment.associatedBracketSegmentId) {
				if (
					isFocusableBrace(focusedEntity) &&
					(focusedEntity.firstBraceId === segment.fileAndOffsetId ||
						focusedEntity.pairedBraceId === segment.fileAndOffsetId)
				) {
					newFocusedEntity = null;
				} else {
					newFocusedEntity = {
						firstBraceId: segment.fileAndOffsetId,
						pairedBraceId: segment.associatedBracketSegmentId
					};
				}
			} else if (focusedEntity === segment.text) {
				newFocusedEntity = '';
			} else {
				newFocusedEntity = segment.text;
			}
			set({ focusedEntity: newFocusedEntity });
		}
	}));

/**
 * Handles the focus state of a code token. It returns whether the token is currently focused and allows to toggle the
 * focus for the given token.
 */
export function useCodeTokenFocus(segment: LineSegment): [boolean, () => void] {
	const { language, focusedCodeTokenStore } = useRenderedCodeOptions();
	const isFocused = useStore(focusedCodeTokenStore, state => isEntityFocused(state.focusedEntity, segment, language));
	const toggleEntityFocus = () => focusedCodeTokenStore.getState().toggleEntityFocus(segment);
	return [isFocused, toggleEntityFocus];
}

/** Extracts the line numbers that should be drawn as highlighted in the code outline bar. */
export function useFocusedElementLines() {
	const { linesWithTokens, focusedCodeTokenStore } = useRenderedCodeOptions();
	const focusedEntity = useStore(focusedCodeTokenStore, state => state.focusedEntity);
	return useMemo(() => getHighlightedLines(focusedEntity, linesWithTokens), [focusedEntity, linesWithTokens]);
}

function isFocusableBrace(focusedEntity: FocusableEntity | null): focusedEntity is FocusableBrace {
	return focusedEntity != null && typeof focusedEntity !== 'string';
}

function isEntityFocused(focusedEntity: FocusableEntity | null, segment: LineSegment, language: ELanguage | undefined) {
	return (
		isSameIdentifier(focusedEntity, segment.text, language) ||
		isDefineStatementOfFocusedMacro(focusedEntity, segment.text, language) ||
		(isFocusableBrace(focusedEntity) &&
			(focusedEntity.firstBraceId === segment.fileAndOffsetId ||
				focusedEntity.pairedBraceId === segment.fileAndOffsetId))
	);
}

function isDefineStatementOfFocusedMacro(
	focusedEntity: FocusableEntity | null,
	segmentText: string,
	language?: ELanguage
): boolean {
	if (!focusedEntity || typeof focusedEntity !== 'string' || !language) {
		return false;
	}
	if (!ELanguage.LANGUAGES_WITH_PREPROCESSOR_EXPANSIONS.includes(language)) {
		return false;
	}
	return segmentText.match('#define\\s+' + RegexUtils.quote(focusedEntity)) != null;
}

/**
 * Returns whether the given entity (if it is string) is the same identifier as identifierText considering some
 * language-specific properties such as case-sensitivity.
 */
export function isSameIdentifier(
	focusedEntity: FocusableEntity | null,
	identifierText: string,
	language?: ELanguage
): boolean {
	if (!focusedEntity || typeof focusedEntity !== 'string' || !language) {
		return false;
	}
	let highlightedIdentifierText = focusedEntity;
	if (language.name === ELanguage.ABAP.name) {
		highlightedIdentifierText = normalizeAbapIdentifier(highlightedIdentifierText);
		identifierText = normalizeAbapIdentifier(identifierText);
	}
	return (
		escapeIdentifier(highlightedIdentifierText, !language.caseSensitive) ===
		escapeIdentifier(identifierText, !language.caseSensitive)
	);
}

function getHighlightedLines(
	focusedEntity: FocusableEntity | null,
	linesWithTokens: Map<number, LineSegment[]>
): number[] {
	const highlightedLines: number[] = [];
	if (focusedEntity) {
		for (const [lineNumber, segments] of linesWithTokens.entries()) {
			if (segments.some(segment => focusMatchesSegment(segment, focusedEntity))) {
				highlightedLines.push(lineNumber);
			}
		}
	}
	return highlightedLines;
}

function focusMatchesSegment(segment: LineSegment, focusedEntity: FocusableEntity) {
	if (isFocusableBrace(focusedEntity)) {
		return (
			focusedEntity.firstBraceId === segment.fileAndOffsetId ||
			focusedEntity.pairedBraceId === segment.fileAndOffsetId
		);
	} else {
		return isCodeLineSegment(segment) && segment.isIdentifier && segment.text === focusedEntity;
	}
}

/**
 * Remove any leading "!" or "@" from the identifier and, if the identifier contains a dash, extract the part before the
 * dash.
 *
 * ABAP identifiers can be prefixed by a "!" or "@" but still refer to the same identifier as well as contain a dash
 * which can be used as an access operator. In order to highlight all possibilities when clicking on an identifier we
 * must normalize all identifiers to a common denominator.
 */
function normalizeAbapIdentifier(name: string): string {
	if (['!', '@'].some(prefix => name.startsWith(prefix))) {
		name = name.substring(1);
	}
	return name.split('-')[0]!;
}

/**
 * Prepends '#' to the given identifier. The addition of prefix with '#' to ensure that the code do not accidentally use
 * JS keywords as
 *
 * Property name which is not allowed in JS.
 *
 * @param identifier Input identifier
 * @returns The prepended identifier
 */
function escapeIdentifier(identifier: string, isCaseInsensitive: boolean): string {
	if (isCaseInsensitive) {
		identifier = identifier.toLowerCase();
	}
	return '#' + identifier;
}
